import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabaseClient';
import BlogHero from '../components/blog/BlogHero';
import BlogGrid from '../components/blog/BlogGrid';

export default function Blog() {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('created_at', { ascending: false });
      setPosts(data || []);
      setLoading(false);
    };
    load();
  }, []);

  // Dynamic values from latest post
  const latestPost    = posts[0];
  const latestTitle   = latestPost?.title || 'Sneaker Culture, Drops & Street Style';
  const latestExcerpt = latestPost?.excerpt || 'Stories from the streets of South Africa. Drop news, culture, style and everything in between from SA Dropped.';
  const latestImage   = latestPost?.cover_image || 'https://www.sadropped.co.za/og-image.jpg';


  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh',
      }}>
        <div style={{
          width: 36, height: 36,
          border: '3px solid #e8e8e8',
          borderTopColor: '#da291c',
          borderRadius: '50%',
          animation: 'spin .7s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        {/* ── Core ── */}
        <title>The Drop Journal | SA Dropped — Sneaker Culture & Stories</title>
        <meta name="description" content={`${latestExcerpt.slice(0, 155)}...`} />
        <meta name="keywords" content="sneaker blog South Africa, SA Dropped blog, sneaker culture SA, streetwear stories, sneaker drops SA, Jordan culture, Nike stories South Africa, Adidas SA, Durban street style" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.sadropped.co.za/blog" />

        {/* ── Open Graph — dynamic from latest post ── */}
        <meta property="og:type" content="blog" />
        <meta property="og:site_name" content="SA Dropped" />
        <meta property="og:title" content={`${latestTitle} | SA Dropped Journal`} />
        <meta property="og:description" content={latestExcerpt} />
        <meta property="og:url" content="https://www.sadropped.co.za/blog" />
        <meta property="og:image" content={latestImage} />
        <meta property="og:image:alt" content="SA Dropped — The Drop Journal" />
        <meta property="og:locale" content="en_ZA" />

        {/* ── Twitter ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@sa_dropped" />
        <meta name="twitter:title" content={`${latestTitle} | SA Dropped Journal`} />
        <meta name="twitter:description" content={latestExcerpt} />
        <meta name="twitter:image" content={latestImage} />

        {/* ── Schema.org ── */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "The Drop Journal — SA Dropped",
            "description": "Sneaker culture, drop news, street style stories and more from SA Dropped, South Africa's premier sneaker destination.",
            "url": "https://www.sadropped.co.za/blog",
            "inLanguage": "en-ZA",
            "publisher": {
              "@type": "Organization",
              "name": "SA Dropped",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.sadropped.co.za/logo_nav.png"
              },
              "sameAs": [
                "https://www.instagram.com/sa_dropped"
              ]
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://www.sadropped.co.za"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": "https://www.sadropped.co.za/blog"
                }
              ]
            },
            "blogPost": ${JSON.stringify(
              posts.slice(0, 5).map(post => ({
                "@type": "BlogPosting",
                "headline": post.title,
                "description": post.excerpt || '',
                "image": post.cover_image || 'https://www.sadropped.co.za/og-image.jpg',
                "url": `https://www.sadropped.co.za/blog/${post.slug}`,
                "datePublished": post.created_at,
                "author": {
                  "@type": "Organization",
                  "name": post.author || "SA Dropped"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "SA Dropped"
                }
              }))
            )}
          }
        `}</script>
      </Helmet>

      <main>
        <BlogHero featured={posts[0]} />
        <BlogGrid posts={posts} />
      </main>
    </>
  );
}